import { pathOr } from 'ramda';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import type {
  ChatChannel,
  ChatChannelType,
  ChatAnnotation,
  ChatShare,
  ChatComment,
  ChatSupplierMscSettings,
} from '@frontend/api';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import notificationService from 'services/notificationService';
import partLibraryService, {
  type AnnotationData,
} from 'services/partLibraryService';

import type { RootState } from 'store/configureStore';
import { getFeatureFlagValue } from 'store/session/selectors';
import { getTeamMembers } from 'store/supplier/sagas';
import type { ActionOfType } from 'store/types';

import type { AnnotationContext } from '../annotationActions/annotationContext';
import { entitiesStore } from '../entities/actions';
import {
  getAnnotationById,
  getChannelById,
  getInternalTeamChatChannelId,
  getReqsReviewChatChannelId,
  getMSCChatChannelId,
  getNormalizedChannelById,
} from '../entities/reducer';
import type { NormalizedChatChannel } from '../entities/types';
import { taskAssignment } from '../tasks/data/actions';
import { getTaskAssignmentInfo, TaskAssignment } from '../tasks/data/sagas';
import {
  channelsList,
  chatDrawer,
  chatDrawerState,
  createAnnotation,
  createComment,
  deleteAnnotation,
  deleteComment,
  editComment,
  editMscChannelConfig,
  fetchSuppliersMscSettings,
  reinitializeTrustedThirdPartyChannel,
  threads,
  types,
  chatApp,
  type ChatAction,
  type ChatInitId,
} from './actions';
import { CHAT_DRAWER_STATE, CHANNEL_TYPES } from './constants';
import {
  getActiveChannelId,
  getDrawerStateByChannelType,
  getPartMasterVersion,
  getTechDataPackageRootUuid,
} from './reducer';
import type {
  ChatDrawerStateType,
  ChatId,
  ChatInitialFocus,
  CommentDraft,
  PartMasterVersion,
} from './types';

type ChatActionOfType<T extends ChatAction['type']> = ActionOfType<
  ChatAction,
  T
>;

export default function* root() {
  yield takeEvery(
    types.ACKNOWLEDGE_CHANNEL_NOTIFICATIONS_REQUEST,
    watchAcknowledgeChannelNotifications,
  );
  yield takeEvery(
    types.ACKNOWLEDGE_THREAD_NOTIFICATIONS_REQUEST,
    watchAcknowledgeThreadNotifications,
  );
  yield takeLatest(types.CREATE_ANNOTATION_REQUEST, watchCreateAnnotation);
  yield takeLatest(types.CREATE_COMMENT_REQUEST, watchCreateComment);
  yield takeLatest(types.CHAT_APP_INIT, watchInitChat);
  yield takeEvery(types.DELETE_ANNOTATION_REQUEST, watchDeleteAnnotation);
  yield takeEvery(types.DELETE_COMMENT_REQUEST, watchDeleteComment);
  yield takeEvery(types.EDIT_COMMENT_REQUEST, watchEditComment);
  yield takeEvery(
    types.EDIT_MSC_CHANNEL_CONFIG_REQUEST,
    watchEditMscChannelConfig,
  );
  yield takeLatest(
    types.FETCH_SUPPLIERS_MSC_SETTINGS_REQUEST,
    watchFetchSuppliersMscSettings,
  );
  yield takeLatest(
    types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_REQUEST,
    watchReinitializeTrustedThirdPartyChat,
  );
}

type CreateAnnotationInChannelParams = {
  comment: CommentDraft;
  channelId: ChatId;
  channelType: ChatChannelType;
  actionUuid: string | null | undefined;
  commentedOnQuoteItemId: number | null | undefined;
  passedPartMasterVersion: PartMasterVersion | null | undefined;
  /** What the page added, collected before the request; chat does not read it. */
  context: AnnotationContext | undefined;
};

function* createAnnotationInChannel({
  comment,
  channelId,
  channelType,
  actionUuid,
  commentedOnQuoteItemId,
  passedPartMasterVersion,
  context,
}: CreateAnnotationInChannelParams) {
  // handle task assignment
  let task: TaskAssignment | undefined;
  if (channelType === CHANNEL_TYPES.PRIMARY) {
    task = yield call(getTaskAssignmentInfo);
  }

  // Use passed partMasterVersion with fallback to selector
  const selectorPartMasterVersion: ReturnType<typeof getPartMasterVersion> =
    yield select(getPartMasterVersion);
  const partMasterVersion =
    passedPartMasterVersion || selectorPartMasterVersion || 1;

  // The sender's own quote item wins over the one the page suggests.
  const finalCommentedOnQuoteItemId =
    commentedOnQuoteItemId || context?.commentedOnQuoteItemId || null;

  const annotation: AnnotationData = {
    ...context,
    initialComment: comment,
    partMasterVersion,
    ...(task && { task }),
    ...(actionUuid && { actionUuid }),
    // NB: the `undefined` overrides a `null` from `context`, so the field is
    // left out of the request rather than sent as null.
    ...(finalCommentedOnQuoteItemId
      ? { commentedOnQuoteItemId: finalCommentedOnQuoteItemId }
      : { commentedOnQuoteItemId: undefined }),
  };

  const response: ChatAnnotation = yield call(
    partLibraryService.annotations.create,
    channelId,
    annotation,
  );

  if (task) {
    // clear tasks
    yield put(taskAssignment.reset());
  }

  return response;
}

function* watchAcknowledgeChannelNotifications(
  action: ChatActionOfType<
    typeof types.ACKNOWLEDGE_CHANNEL_NOTIFICATIONS_REQUEST
  >,
) {
  try {
    const { channelId } = action.payload;
    yield notificationService.chat.acknowledgeChannelNotifications(channelId);
  } catch (e) {
    console.error('Failed to acknowledge channel notifications', e);
  }
}

function* watchAcknowledgeThreadNotifications(
  action: ChatActionOfType<
    typeof types.ACKNOWLEDGE_THREAD_NOTIFICATIONS_REQUEST
  >,
) {
  try {
    const { annotationId } = action.payload;
    yield notificationService.chat.acknowledgeThreadNotifications(annotationId);
  } catch (e) {
    console.error('Failed to acknowledge thread notifications', e);
  }
}

function* watchCreateAnnotation(
  action: ChatActionOfType<typeof types.CREATE_ANNOTATION_REQUEST>,
) {
  try {
    const {
      channelType,
      comment,
      actionUuid,
      partMasterUuid,
      commentedOnQuoteItemId,
      partMasterVersion,
      requestId,
      context,
    } = action.payload;
    let { channelId } = action.payload;
    const createNewChannel = !channelId;

    const techDataPackageRootUuid: ReturnType<
      typeof getTechDataPackageRootUuid
    > = yield select(getTechDataPackageRootUuid);

    // Use techDataPackageRootUuid for viewer context, fallback to partMasterUuid for BAQ context
    const finalPartUuid = techDataPackageRootUuid || partMasterUuid;

    if (createNewChannel) {
      if (!finalPartUuid) {
        throw new Error('No part to create the chat channel for');
      }
      // create a channel if none exist
      // Use passed partMasterVersion with fallback to selector
      const selectorPartMasterVersion: ReturnType<typeof getPartMasterVersion> =
        yield select(getPartMasterVersion);
      const finalPartMasterVersion =
        partMasterVersion || selectorPartMasterVersion || 1;
      const newChannelResponse: ChatChannel =
        yield partLibraryService.channels.create(finalPartUuid, {
          partMasterVersion: finalPartMasterVersion,
          type: channelType,
        });
      channelId = newChannelResponse.id;

      yield put(entitiesStore.receiveChannels([newChannelResponse]));
      // The sender reacts to the new channel (e.g. opening where the page
      // shows it) from an effect, so only after chat has made it active.
      yield put(
        createAnnotation.recordAnnotationCreating({
          channelId: newChannelResponse.id,
          channelType,
          requestId,
        }),
      );

      // FIXME PP-26212: MSC and Penn are placed three different ways: this
      // page mapping, the init focus (MSC → EXTERNAL) and the socket's
      // no-active-channel rule (Penn → TEAM). Reconcile with product.
      const drawerStateByChannelType: ReturnType<
        typeof getDrawerStateByChannelType
      > = yield select(getDrawerStateByChannelType);
      const drawerState = drawerStateByChannelType?.[channelType];
      if (drawerState) {
        yield put(
          chatDrawerState.set({
            activeChannelId: newChannelResponse.id,
            activeChannelType: channelType,
            chatDrawerState: drawerState,
          }),
        );
      }
    }

    const response: ChatAnnotation = yield call(createAnnotationInChannel, {
      comment,
      channelId,
      channelType,
      actionUuid,
      commentedOnQuoteItemId,
      passedPartMasterVersion: partMasterVersion,
      context,
    });

    yield put(entitiesStore.receiveAnnotation(response));
    yield put(createAnnotation.success({ channelId, channelType, requestId }));
  } catch (e) {
    yield put(
      createAnnotation.error({
        channelId: pathOr<ChatId | null>(
          null,
          ['payload', 'channelId'],
          action,
        ),
        requestId: action.payload.requestId,
        error: e,
      }),
    );
  }
}

function* watchCreateComment(
  action: ChatActionOfType<typeof types.CREATE_COMMENT_REQUEST>,
) {
  try {
    const { annotationId, comment } = action.payload;

    const response: ChatComment = yield call(
      partLibraryService.comments.create,
      annotationId,
      { content: comment, parentId: null },
    );

    yield put(entitiesStore.receiveComment(response));
    yield put(createComment.success({ annotationId }));
  } catch (e) {
    yield put(
      createComment.error(
        e,
        pathOr<number | null>(null, ['payload', 'annotationId'], action),
      ),
    );
  }
}

function* watchDeleteAnnotation(
  action: ChatActionOfType<typeof types.DELETE_ANNOTATION_REQUEST>,
) {
  try {
    const { annotationId } = action.payload;
    yield call(partLibraryService.annotations.delete, annotationId);
    yield put(deleteAnnotation.success());
  } catch (error) {
    yield put(deleteAnnotation.error(error));
  }
}

function* watchDeleteComment(
  action: ChatActionOfType<typeof types.DELETE_COMMENT_REQUEST>,
) {
  try {
    const { commentId } = action.payload;
    yield call(partLibraryService.comments.delete, commentId);
    yield put(deleteComment.success());
  } catch (error) {
    yield put(deleteComment.error(error));
  }
}

function* watchEditComment(
  action: ChatActionOfType<typeof types.EDIT_COMMENT_REQUEST>,
) {
  try {
    const { commentId, content } = action.payload;
    const response: ChatComment = yield call(
      partLibraryService.comments.patch,
      commentId,
      {
        content,
      },
    );

    yield put(entitiesStore.receiveComment(response));
    yield put(editComment.success({ commentId }));
  } catch (error) {
    const commentId = pathOr<ChatId | null>(
      null,
      ['payload', 'commentId'],
      action,
    );
    yield put(editComment.error(error, commentId));
  }
}

function* watchEditMscChannelConfig(
  action: ChatActionOfType<typeof types.EDIT_MSC_CHANNEL_CONFIG_REQUEST>,
) {
  try {
    const { mscChannelId, mscSpecialistId } = action.payload;
    const channel: ChatChannel = yield call(
      partLibraryService.mscChannelConfig.patch,
      mscChannelId,
      { mscSpecialistId },
    );
    yield put(entitiesStore.receiveChannel(channel));
    yield put(editMscChannelConfig.success());
  } catch (error) {
    yield put(editMscChannelConfig.error(error));
  }
}

function* watchFetchChannels() {
  try {
    const techDataPackageRootUuid: ReturnType<
      typeof getTechDataPackageRootUuid
    > = yield select(getTechDataPackageRootUuid);
    const results: ChatChannel[] = yield call(
      partLibraryService.channels.list,
      techDataPackageRootUuid,
    );

    yield put(channelsList.success());
    yield put(entitiesStore.receiveChannels(results));
  } catch (e) {
    console.error('Error in watchFetchChannels', e);
    yield put(channelsList.error());
  }
}

/**
 * Opens a channel the initial focus asked for. The page that started chat
 * hears of it from an effect, so only after chat has made it active.
 */
function* focusInitialChannel(
  initId: ChatInitId,
  channel: NormalizedChatChannel,
) {
  if (!channel.type) {
    return;
  }
  yield put(
    chatApp.initialChannelFocused({
      initId,
      channelId: channel.id,
      channelType: channel.type,
    }),
  );
  const drawerStateByChannelType: ReturnType<
    typeof getDrawerStateByChannelType
  > = yield select(getDrawerStateByChannelType);
  const drawerState = drawerStateByChannelType?.[channel.type];
  if (drawerState) {
    yield put(
      chatDrawerState.set({
        activeChannelId: channel.id,
        activeChannelType: channel.type,
        chatDrawerState: drawerState,
      }),
    );
  }
}

/** Opens one of the part's own channels in the given drawer tab, if it has it. */
function* openPartChannel(
  getChannelId: (state: RootState) => ChatId | null | undefined,
  channelType: ChatChannelType,
  drawerState: ChatDrawerStateType,
) {
  const channelId: ReturnType<typeof getChannelId> = yield select(getChannelId);
  yield put(
    chatDrawerState.set({
      activeChannelId: channelId ?? null,
      activeChannelType: channelType,
      chatDrawerState: drawerState,
    }),
  );
}

function* applyInitialFocus(initId: ChatInitId, focus: ChatInitialFocus) {
  switch (focus.kind) {
    case 'annotation': {
      const annotation: ReturnType<typeof getAnnotationById> = yield select(
        getAnnotationById,
        focus.annotationId,
      );
      const channel: ReturnType<typeof getNormalizedChannelById> = annotation
        ? yield select(getNormalizedChannelById, annotation.channelId)
        : null;
      if (annotation && channel) {
        yield call(focusInitialChannel, initId, channel);
        // only show annotation if it is not soft deleted
        if (annotation.isLive) {
          yield put(
            threads.setActive({
              annotationId: focus.annotationId,
              channelId: annotation.channelId,
            }),
          );
        }
        yield put(chatDrawer.open());
      }
      return;
    }
    case 'channel': {
      const channel: ReturnType<typeof getNormalizedChannelById> = yield select(
        getNormalizedChannelById,
        focus.channelId,
      );
      if (channel) {
        yield call(focusInitialChannel, initId, channel);
        yield put(chatDrawer.open());
      }
      return;
    }
    case 'mscChannel':
      // FIXME PP-26212: EXTERNAL here, but a new MSC channel on the viewer
      // opens in TEAM (its page mapping). Reconcile with product.
      yield call(
        openPartChannel,
        getMSCChatChannelId,
        CHANNEL_TYPES.MSC,
        CHAT_DRAWER_STATE.EXTERNAL,
      );
      return;
    case 'teamChannel':
      yield call(
        openPartChannel,
        getInternalTeamChatChannelId,
        CHANNEL_TYPES.PRIMARY,
        CHAT_DRAWER_STATE.TEAM,
      );
      return;
    case 'reqsReviewChannel':
      yield call(
        openPartChannel,
        getReqsReviewChatChannelId,
        CHANNEL_TYPES.ACTIONS,
        CHAT_DRAWER_STATE.REQS_REVIEW,
      );
      return;
    case 'noChannel':
      yield put(
        chatDrawerState.set({
          activeChannelId: null,
          activeChannelType: null,
          chatDrawerState: CHAT_DRAWER_STATE.EXTERNAL,
        }),
      );
      return;
  }
}

/**
 * Starts chat for a part: its channels and team, then the focus the page asked
 * for. Starting chat changes nothing else; a page that wants chat fresh resets
 * it first.
 */
function* watchInitChat(action: ChatActionOfType<typeof types.CHAT_APP_INIT>) {
  const { initId, initialFocus } = action.payload;

  yield all([call(watchFetchChannels), call(getTeamMembers)]);

  const isMscChatAvailable: ReturnType<typeof getFeatureFlagValue> =
    yield select(getFeatureFlagValue, featureFlagKeys.isMscChatAvailable);
  if (isMscChatAvailable) {
    yield put(fetchSuppliersMscSettings.request());
  }

  yield call(applyInitialFocus, initId, initialFocus);
}

function* watchFetchSuppliersMscSettings() {
  try {
    const response: ChatSupplierMscSettings = yield call(
      partLibraryService.suppliersMscSettings.get,
    );
    yield put(
      fetchSuppliersMscSettings.success({ suppliersMscSettings: response }),
    );
  } catch (error) {
    console.error('Failed to fetch suppliers MSC settings', error);
    yield put(fetchSuppliersMscSettings.error(error));
  }
}

function* watchReinitializeTrustedThirdPartyChat() {
  try {
    const channelId: ReturnType<typeof getActiveChannelId> = yield select(
      getActiveChannelId,
    );
    const channel: ReturnType<typeof getChannelById> = yield select(
      getChannelById,
      channelId,
    );
    const partMasterVersion: ReturnType<typeof getPartMasterVersion> =
      yield select(getPartMasterVersion);
    if (
      channel.type !== CHANNEL_TYPES.MSC &&
      channel.type !== CHANNEL_TYPES.PENN
    ) {
      console.error(
        'ATTEMPTED TO REINITIALIZE NON-TRUSTED THIRD PARTY CHANNEL',
      );
      throw new Error('Invalid channel.');
    }
    const lastShare = pathOr<ChatShare | null>(null, ['shares', 0], channel);
    yield partLibraryService.shares.create(channelId, {
      emailAddress: lastShare.sharedWithEmail,
      emailSubjectLine: lastShare.emailSubjectLine,
      message: null,
      partMasterVersion,
    });
    yield put(reinitializeTrustedThirdPartyChannel.success());
  } catch (error) {
    yield put(reinitializeTrustedThirdPartyChannel.error(error));
  }
}
